import { useAccountState } from "@/composables/useAccountState";
import { useUnreadComments } from "@/composables/useComments";
import { useUserStore } from "@/store/user";
import { getProviderType, isOpeningFormSent, type Account } from "@aircap/common/account";
import { supervisorRoles } from "@aircap/common/auth";
import type { Ref } from "vue";
import { computed } from "vue";
import type { MessageKey } from "./useI18n";

// Single definition of an account's navigation, rendered by the side menu. The active tab is the
// route itself, so the menu renders `:to` links from this list.
export const useAccountTabs = (account: Ref<(Account & { readonly id: string }) | undefined>) => {
  const user = useUserStore();
  const { accountState, accountStateUi } = useAccountState(account);
  // Operators have no comment reader, and open-banking accounts no comments
  const { unreadCountOfType } = useUnreadComments(() =>
    account.value &&
    getProviderType(account.value.accountType) === "proAccount" &&
    !user.isKind("operator")
      ? account.value.id
      : undefined,
  );

  const treezorOpening = computed(
    () => account.value?.accountType === "treezor" && accountState.value?.status !== "synced",
  );
  const holderOnboarding = computed(
    () =>
      user.isKind("user") &&
      treezorOpening.value &&
      !!account.value &&
      !isOpeningFormSent(account.value),
  );

  const tabs = computed(() => {
    if (!account.value) return [];
    const proAccount = getProviderType(account.value.accountType) === "proAccount";
    const openBanking = getProviderType(account.value.accountType) === "openBanking";
    const supervisionPending =
      !user.isKind("admin") &&
      account.value.accountType === "treezor" &&
      (treezorOpening.value || !account.value.supervisorOrgId);
    const tabs: {
      name: string;
      title: MessageKey;
      icon: string;
      badge: number;
      unreadComments: number;
      visible: boolean;
    }[] = [
      {
        name: "Opening",
        title: user.isKind("admin") ? "account.tabs.kyc" : "sidemenu.accountOpening",
        icon: user.isKind("admin") ? "mdi-shield-outline" : "mdi-file-document-edit-outline",
        badge: 0,
        unreadComments: 0,
        visible: holderOnboarding.value || (treezorOpening.value && user.isKind("admin")),
      },
      {
        name: "Dashboard",
        title: "account.tabs.dashboard",
        icon: "mdi-view-dashboard-outline",
        badge: 0,
        unreadComments: 0,
        visible: true,
      },
      {
        name: "Transactions",
        title: "account.tabs.transactions",
        icon: "mdi-swap-horizontal",
        badge: 0,
        unreadComments: unreadCountOfType("transaction"),
        visible: true,
      },
      {
        name: "TransferRequests",
        title: "account.tabs.transfersToValidate",
        icon: "mdi-bank-transfer",
        badge: account.value.pendingTransfersCount ?? 0,
        unreadComments: unreadCountOfType("transferRequest"),
        visible: proAccount && !supervisionPending,
      },
      {
        name: "Beneficiaries",
        title: "account.tabs.beneficiaries",
        icon: "mdi-account-multiple-outline",
        badge: 0,
        unreadComments: 0,
        visible: proAccount && !supervisionPending,
      },
      {
        name: "Notifications",
        title: "account.tabs.notifications",
        icon: "mdi-bell-outline",
        badge: 0,
        unreadComments: 0,
        visible: user.hasRole(supervisorRoles, { account: account.value }) && openBanking,
      },
      {
        name: "Budget",
        title: "account.tabs.budget",
        icon: "mdi-chart-pie",
        badge: 0,
        unreadComments: 0,
        visible: true,
      },
      {
        name: "Commercialisation",
        title: "account.tabs.commercialisation",
        icon: "mdi-storefront-outline",
        badge: 0,
        unreadComments: 0,
        visible: true,
      },
      {
        name: "Logs",
        title: "account.tabs.logs",
        icon: "mdi-text-box-outline",
        badge: 0,
        unreadComments: 0,
        visible: user.isKind("admin"),
      },
      {
        name: "Settings",
        title: "account.tabs.settings",
        icon: "mdi-cog-outline",
        badge: 0,
        unreadComments: 0,
        visible: user.hasRole(supervisorRoles, { account: account.value }),
      },
    ];
    // A holder still filling in the opening form only gets that form
    return tabs.filter((tab) => tab.visible && (!holderOnboarding.value || tab.name === "Opening"));
  });

  return {
    accountState,
    accountStateUi,
    treezorOpening,
    holderOnboarding,
    tabs,
  };
};
